import { Input, Form, Card, message, Select, Tag, Tabs, Alert, Timeline, Empty, Divider, Button, Flex } from 'antd';
import { useContext, useEffect, useState } from 'react';
import ContactEditionFormContext from '../../contexts/forms/contacts/ContactEditionFormContext';
import { FormCard, READ_ONLY_INPUT_STYLE, FormContainer, FormSectionTitle } from '@leedpulse-monorepo/components';
import ContactsContext from '../../contexts/data/ContactsContext';
import ScenariosContext from '../../contexts/data/ScenariosContext';
import { CONTACTS_PAGE_PATH } from '../../pages/contacts/ContactsPage';
import { SUBSCRIPTIONS_PAGE_PATH } from '../../pages/subscriptions/SubscriptionsPage';
import { COMPANIES_PAGE_PATH } from '../../pages/companies/CompaniesPage';
import { UserRole, ContextStatus, ContactHistoryType, contactHistoryTypeAsString, channelAsString, ETaskStatus, EContactCivility, contactCivilityAsString } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import Subscription from '../../../domain/models/Subscription';
import UserDoc from '../../../domain/models/UserDoc';
import { getTagColor } from '../../../core/utils/commonUtils';
import { getInterestLevel, getSenderCountryFlag } from '@leedpulse-monorepo/utils';
import Scenario from '../../../domain/models/Scenario';
import NumbersContext from '../../contexts/data/NumbersContext';
import TagsContext from '../../contexts/data/TagsContext';
import TagModel from '../../../domain/models/Tag';
import moment from 'moment';
import { contactHistoryTypeAsIcon } from '../../../domain/enums/contact/contact_history_type/ContactHistoryType';
import { handleErrorInputMessage, handleInputError } from '../../../core/utils/errorUtils';
import TasksContext from '../../contexts/data/TasksContext';
import TasksList from '../@shared/TasksList';
import CallsList from '../@shared/CallsList';
import { useNavigate } from 'react-router-dom';
import { ICompany, IContact } from '@leedpulse-monorepo/interfaces';
import { CalendarOutlined, CheckCircleOutlined, FormOutlined, HistoryOutlined, HourglassOutlined, InfoCircleOutlined, PhoneOutlined } from '@ant-design/icons';
import { FirestoreCollections } from '@leedpulse-monorepo/firebase-metadata';

const MAX_HISTORY_INFO_LENGTH = 150;

interface ContactDetailsProps {
  contact: IContact;
  subscription: Subscription | null;
  // Set instead of subscription for a Leedflow contact without a number: its id_api_key is its company's (LEED-2463)
  company?: ICompany | null;
  subscriptionId?: string | null;
  globalScenarios?: Scenario[];
  owners: UserDoc[];
}

// NOTE : We could improve the usage of subscriptionId, and only
// use some kind of flag to determine if we are in the context of a 'from subscription'
const ContactDetails: React.FC<ContactDetailsProps> = ({
  contact,
  subscription,
  company = null,
  subscriptionId,
  globalScenarios = [],
  owners,
}) => {
  const navigate = useNavigate();
  const contactEditionFormContext = useContext(ContactEditionFormContext);
  const contactsContext = useContext(ContactsContext);
  const scenariosContext = useContext(ScenariosContext);
  const numbersContext = useContext(NumbersContext);
  const tagsContext = useContext(TagsContext);
  const tasksContext = useContext(TasksContext);
  const authContext = useContext(AuthenticationContext);

  const [scenarios, setScenarios] = useState([] as Scenario[]);
  const [expandedHistoryItems, setExpandedHistoryItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (contactEditionFormContext.wipContact === null) {
      contactEditionFormContext.initWithContact(contact);
      if (scenariosContext.scenarios.length === 0) {
        scenariosContext.fetchByIds(subscription?.idScenarios ?? []);
      } else {
        // eslint-disable-next-line react/set-state-in-effect -- mirrors scenariosContext (external system) already loaded on mount
        setScenarios(scenariosContext.scenarios);
      }
      if (subscription) {
        numbersContext.fetchFromSenders(subscription.sender);
        tagsContext.fetch(null, subscriptionId ?? null);
      }
      tasksContext.fetch(ETaskStatus.Completed, contact.id);
      tasksContext.fetch(ETaskStatus.ToDo, contact.id);
    }

    return () => {
      scenariosContext.reset();
    }
  }, []);

  useEffect(() => {
    if (scenariosContext.status === ContextStatus.SUCCESS) {
      // eslint-disable-next-line react/set-state-in-effect -- mirrors scenariosContext (external system) once its fetch resolves
      setScenarios(scenariosContext.scenarios);
    }
  }, [scenariosContext.scenarios, scenariosContext.status]);

  useEffect(() => {
    switch (contactEditionFormContext.updateStatus) {
      case ContextStatus.SUCCESS:
        message.success('Mise à jour du contact avec succès');
        break;
      case ContextStatus.ERROR:
        message.error(contactEditionFormContext.errorMessage);
        break;
      default:
        break;
    }
  }, [contactEditionFormContext.updateStatus]);

  useEffect(() => {
    switch (contactEditionFormContext.deleteStatus) {
      case ContextStatus.SUCCESS:
        contactsContext.refresh();
        message.success('Contact supprimé avec succès');
        navigate(
          subscriptionId
            ? SUBSCRIPTIONS_PAGE_PATH +
            '/' +
            subscriptionId +
            CONTACTS_PAGE_PATH
            : CONTACTS_PAGE_PATH
        );

        break;
      case ContextStatus.ERROR:
        message.error('Erreur lors de la suppression du contact');
        break;
      default:
        break;
    }
  }, [contactEditionFormContext.deleteStatus]);

  const isBusy =
    contactEditionFormContext.updateStatus === ContextStatus.LOADING ||
    contactEditionFormContext.deleteStatus === ContextStatus.LOADING;

  const isReadOnly =
    authContext.authentication == null ||
    ![UserRole.owner, UserRole.admin, UserRole.maintainer].includes(
      authContext.authentication?.role
    );

  const wipContact = contactEditionFormContext.wipContact;

  return wipContact === null ? (
    <Card loading />
  ) : (
    <FormCard
      title={wipContact.id}
      collectionName={FirestoreCollections.contacts}
      onDeleted={
        isReadOnly ? undefined : () => contactEditionFormContext.submitDelete()
      }
      onUpdated={
        isReadOnly ? undefined : () => contactEditionFormContext.submitUpdate()
      }
      isUpdateLoading={isBusy}
      isDeleteLoading={isBusy}
    >
      <Tabs defaultActiveKey='1'>
        <Tabs.TabPane key={1} tab={<Flex align="center"><InfoCircleOutlined style={{ marginRight: 6 }} /> Informations générales</Flex>}>
          <Form
            layout="vertical"
            initialValues={{ remember: true }}
            requiredMark={false}
          >
            <FormContainer oneColumnMax>
              <Form.Item style={{ marginTop: '0.5rem' }}>
                <Tag className='contact-tile__content__owner-tag' color={getTagColor(wipContact?.qualification_ai?.qualification)}>
                  &#8226; {getInterestLevel(wipContact?.qualification_ai?.qualification || "not_available")}
                </Tag>
                <Tag className='contact-tile__content__owner-tag' color='lightgray'>
                  {scenarios.find(scenario => scenario.id === wipContact?.contact?.prospect_category)?.name || wipContact?.contact?.prospect_category}
                </Tag>
                {tagsContext.tags
                  .filter((t: TagModel) => Object.keys(wipContact.tags ?? {}).includes(t.id))
                  .map((tag: TagModel) => (
                    <Tag key={tag.id} color={tag.color} style={{ color: '#FFFFFF' }}>
                      {tag.name}
                    </Tag>
                  ))}
              </Form.Item>
              <Form.Item label={<Flex align="center"><CalendarOutlined style={{ marginRight: 6 }} /> Abonnement</Flex>}>
                {subscription == null ? (
                  <Flex vertical gap={4}>
                    <Flex align="center" gap={6} wrap>
                      <Tag color='orange'>Sans numéro</Tag>
                      <span>Clé API de l'entreprise</span>
                      <Tag
                        className='contact-tile__content__owner-tag'
                        style={{ cursor: 'pointer' }}
                        color='geekblue'
                        onMouseDown={(e) => {
                          if (e.button === 1 && company) {
                            e.preventDefault();
                            window.open(COMPANIES_PAGE_PATH + '/' + company.id, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        onClick={(e) => {
                          if (!company) return;
                          const url = COMPANIES_PAGE_PATH + '/' + company.id;
                          if (e?.ctrlKey || e?.metaKey) {
                            window.open(url, '_blank', 'noopener,noreferrer');
                            return;
                          }
                          navigate(url);
                        }}
                      >
                        {company?.name || company?.id}
                      </Tag>
                      <span>, pas d'un abonnement</span>
                    </Flex>
                    <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                      {contact.id_api_key} : la ligne de son owner est partie sans lui. Un superviseur le rattache depuis le portail, Contacts &gt; Sans numéro.
                    </span>
                  </Flex>
                ) : (
                <Tag
                  className='contact-tile__content__owner-tag'
                  style={{ cursor: 'pointer' }}
                  color='geekblue'
                  onMouseDown={(e) => {
                    if (e.button === 1) {
                      e.preventDefault();
                      window.open(SUBSCRIPTIONS_PAGE_PATH + '/' + subscription.id, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  onClick={(e) => {
                    const isCtrlOrCmdClick = e?.ctrlKey || e?.metaKey;
                    const url = SUBSCRIPTIONS_PAGE_PATH + '/' + subscription.id;

                    if (isCtrlOrCmdClick) {
                      window.open(url, '_blank', 'noopener,noreferrer');
                      return;
                    }
                    navigate(url);
                  }}
                >
                  {subscription.name}
                </Tag>
                )}
              </Form.Item>
            </FormContainer>
            <Divider />
            <FormContainer>
              <Form.Item label="Civilité">
                <Select
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  disabled={isBusy || isReadOnly}
                  onChange={(value) => {
                    contactEditionFormContext.onCivilityChange(value as string);
                  }}
                  value={wipContact.contact.civility || ""}
                >
                  {Object.values(EContactCivility).map((civility) => (
                    <Select.Option key={civility} value={civility}>
                      {contactCivilityAsString(civility)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Prénom"
                validateStatus={
                  handleInputError(wipContact.contact.firstname, contactEditionFormContext.updateStatus)
                }
                help={
                  handleErrorInputMessage(wipContact.contact.firstname, contactEditionFormContext.updateStatus)
                }>
                <Input
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    contactEditionFormContext.onFirstnameChange(e.target.value);
                  }}
                  value={wipContact.contact.firstname}
                  status={wipContact.contact.firstname === '' ? 'error' : ''}
                />
              </Form.Item>
              <Form.Item
                label="Nom :"
                validateStatus={
                  handleInputError(wipContact.contact.lastname, contactEditionFormContext.updateStatus)
                }
                help={
                  handleErrorInputMessage(wipContact.contact.lastname, contactEditionFormContext.updateStatus)
                }
              >
                <Input
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    contactEditionFormContext.onLastnameChange(e.target.value);
                  }}
                  value={wipContact.contact.lastname}
                  status={wipContact.contact.lastname === '' ? 'error' : ''}
                />
              </Form.Item>
              <Form.Item label="Email">
                <Input
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    contactEditionFormContext.onEmailChange(e.target.value);
                  }}
                  value={wipContact.contact.email}
                />
              </Form.Item>
              <Form.Item label="Entreprise">
                <Input
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    contactEditionFormContext.onCompanyChange(e.target.value);
                  }}
                  value={wipContact.contact.company}
                />
              </Form.Item>
              <Form.Item label="Poste">
                <Input
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    contactEditionFormContext.onRoleChange(e.target.value);
                  }}
                  value={wipContact.contact.role}
                />
              </Form.Item>
              <Form.Item label="Adresse">
                <Input
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    contactEditionFormContext.onAdresseChange(e.target.value);
                  }}
                  value={wipContact.contact.adresse}
                />
              </Form.Item>
              <Form.Item label="Site web">
                <Input
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    contactEditionFormContext.onWebsiteChange(e.target.value);
                  }}
                  value={wipContact.contact.website}
                />
              </Form.Item>
              <Form.Item
                label="Téléphone"
                validateStatus={
                  handleInputError(wipContact.contact.phone, contactEditionFormContext.updateStatus)
                }
                help={
                  handleErrorInputMessage(wipContact.contact.phone, contactEditionFormContext.updateStatus)
                }>
                <Input
                  disabled={true}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    // Remove spaces
                    e.target.value = e.target.value.replace(/\s/g, '');
                    contactEditionFormContext.onPhoneChange(e.target.value);
                  }}
                  value={wipContact.contact.phone}
                  status={wipContact.contact.phone === '' ? 'error' : ''}
                />
              </Form.Item>
              <Form.Item label="Conseiller">
                <Select
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  disabled={isBusy || isReadOnly}
                  onChange={(value) => {
                    contactEditionFormContext.onOwnerChange(value as string);
                  }}
                  value={wipContact.contact.owner}
                >
                  {owners.map((owner) => {
                    return (
                      <Select.Option key={owner.uid} value={owner.uid}>
                        {owner.firstname + ' ' + owner.lastname}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <Form.Item label="N° d'envoyeur">
                <Select
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  disabled={true}
                  loading={numbersContext.sendersStatus === ContextStatus.LOADING}
                  onChange={(value) => {
                    // Remove spaces
                    value = value.replace(/\s/g, '');
                    contactEditionFormContext.onSenderChange(value as string);
                  }}
                  // Empty on a Leedflow contact moved by a number change: its line is the subscription's
                  value={wipContact.contact.sender || subscription?.sender[0]}
                >
                  {numbersContext.items.map((number) => (
                    <Select.Option key={number.sender} value={number.sender}>
                      {`${getSenderCountryFlag(number.sender)} ${number.sender} (${channelAsString(number.channel)}) - ${number.provider}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Scénario">
                <Select
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  disabled={isBusy || isReadOnly}
                  loading={scenariosContext.status === ContextStatus.LOADING}
                  onChange={(value) => {
                    contactEditionFormContext.onProspectCategoryChange(value as string);
                  }}
                  value={wipContact.contact.prospect_category}
                >
                  {[...globalScenarios, ...scenariosContext.scenarios].map((scenario) => (
                    <Select.Option key={scenario.id} value={scenario.id}>
                      {scenario.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </FormContainer>
            <FormContainer oneColumnMax>
              <Form.Item label="Détails">
                <Input.TextArea
                  rows={6}
                  disabled={isBusy}
                  readOnly={isReadOnly}
                  style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
                  onChange={(e) => {
                    contactEditionFormContext.onDetailsChange(e.target.value);
                  }}
                  value={wipContact.contact.details}
                />
              </Form.Item>
            </FormContainer>

            <Form.Item label={"Date de création"}>
              {moment(wipContact.created).format('LLLL') + " (" + moment(wipContact.created).fromNow() + ")"}
            </Form.Item>

            <Form.Item label={"Mise à jour"}>
              {moment(wipContact.updated).format('LLLL') + " (" + moment(wipContact.updated).fromNow() + ")"}
            </Form.Item>
          </Form>
        </Tabs.TabPane>

        <Tabs.TabPane key={2} style={{ maxHeight: '40rem', overflow: 'auto', padding: '0.5rem' }} tab={<Flex align="center"><HistoryOutlined style={{ marginRight: 6 }} /> Historique</Flex>}>
          {wipContact?.history?.length === 0 ?
            <Empty description="L'historique est vide" /> :
            <Timeline mode='left' items={
              wipContact?.history?.map((contactHistory) => {
                const itemKey = contactHistory.timestamp.toString();
                const isExpanded = expandedHistoryItems.has(itemKey);
                const shouldTruncate = contactHistory.info && contactHistory.info.length > MAX_HISTORY_INFO_LENGTH;
                const displayText = shouldTruncate && !isExpanded
                  ? contactHistory.info.substring(0, MAX_HISTORY_INFO_LENGTH) + '...'
                  : contactHistory.info;

                return {
                  dot: contactHistoryTypeAsIcon(contactHistory.type),
                  children: (
                    <>
                      <p><b>{moment(contactHistory.timestamp).format('LLL')}</b></p>
                      <p>{contactHistoryTypeAsString(contactHistory.type as unknown as ContactHistoryType)}</p>
                      {contactHistory.info && (
                        <div
                          style={{
                            backgroundColor: 'var(--primary-color)',
                            color: 'white',
                            borderRadius: '12px',
                            padding: '0.75rem 1rem',
                            marginTop: '0.5rem',
                            display: 'inline-block',
                            maxWidth: '100%',
                            wordWrap: 'break-word'
                          }}
                        >
                          <p style={{ margin: 0 }}>{displayText}</p>
                          {shouldTruncate && (
                            <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                const newExpanded = new Set(expandedHistoryItems);
                                if (isExpanded) {
                                  newExpanded.delete(itemKey);
                                } else {
                                  newExpanded.add(itemKey);
                                }
                                setExpandedHistoryItems(newExpanded);
                              }}
                              style={{
                                padding: 0,
                                height: 'auto',
                                marginTop: '0.5rem',
                                color: 'white',
                              }}
                            >
                              {isExpanded ? 'Voir moins' : 'Voir plus'}
                            </Button>
                          )}
                        </div>
                      )}
                    </>
                  )
                }
              })
            } />
          }
        </Tabs.TabPane>

        <Tabs.TabPane key={3} tab={<Flex align="center"><CheckCircleOutlined style={{ marginRight: 6 }} /> Qualification</Flex>}>
          <Form layout='vertical'>
            <Form.Item label='Statut'>
              <Tag className='contact-tile__content__owner-tag' color={getTagColor(wipContact?.qualification_ai?.qualification)}>
                &#8226; {getInterestLevel(wipContact?.qualification_ai?.qualification || "not_available")}
              </Tag>
            </Form.Item>

            <Form.Item label='Résumé'>
              {wipContact?.qualification_ai?.summary === 'not_available' ?
                <Alert
                  message="Non disponible - la qualification n'a pas été effectuée"
                  type="error"
                  showIcon
                /> :
                <Alert
                  message={wipContact?.qualification_ai?.summary}
                  type="info"
                  showIcon
                />
              }
            </Form.Item>

            <Form.Item label="L'IA a t-elle estimé qu'elle devait répondre suite au dernier message du prospect ?">
              {
                wipContact?.qualification_ai?.reply_needed ?
                  <Alert
                    message="Oui, une réponse a été envoyée"
                    type="success"
                    showIcon
                  /> :
                  <Alert
                    message="Non, aucune réponse n'a été envoyée"
                    type="error"
                    showIcon
                  />
              }
            </Form.Item>

            <Form.Item label="L'IA a t-elle estimé que le conseiller devait être notifié ?">
              {
                wipContact?.qualification_ai?.report_to_owner_needed ?
                  <Alert
                    message="Oui, le conseiller a été notifié"
                    type="success"
                    showIcon
                  /> :
                  <Alert
                    message="La conversation est en cours"
                    type="info"
                    showIcon
                  />
              }
            </Form.Item>

            <Form.Item label='Suppression demandée ?'>
              {
                wipContact?.qualification_ai?.stop_immediat ?
                  <Alert
                    message="Oui, le prospect a demandé à supprimer ses données de la base de données ⚠️"
                    type="error"
                    showIcon
                  /> :
                  <Alert
                    message="Non, le prospect n'a pas demandé à supprimer ses données de la base de données"
                    type="success"
                    showIcon
                  />
              }
            </Form.Item>

            <Form.Item label='Date de la dernière qualification'>
              {moment(wipContact?.qualification_ai?.timestamp).format('LLLL')}
            </Form.Item>
          </Form>
        </Tabs.TabPane>

        <Tabs.TabPane key={4} tab={<Flex align="center"><FormOutlined style={{ marginRight: 6 }} /> Tâches</Flex>}>
          <Form layout='vertical'>
            <div style={{ marginTop: '1rem' }}></div>
            <FormSectionTitle title={<Flex align="center"><HourglassOutlined style={{ marginRight: 6 }} /> Prochainement ({tasksContext.toDoCount})</Flex>}></FormSectionTitle>
            <TasksList scopedContactId={wipContact.id} status={ETaskStatus.ToDo} items={tasksContext.itemsToDo} count={tasksContext.toDoCount}></TasksList>
            <div style={{ marginTop: '2rem' }}></div>
            <FormSectionTitle title={<Flex align="center"><CheckCircleOutlined style={{ marginRight: 6 }} /> Réalisées ({tasksContext.completedCount})</Flex>}></FormSectionTitle>
            <TasksList scopedContactId={wipContact.id} status={ETaskStatus.Completed} items={tasksContext.itemsCompleted} count={tasksContext.completedCount}></TasksList>
          </Form>
        </Tabs.TabPane>

        <Tabs.TabPane key={5} tab={<Flex align="center"><PhoneOutlined style={{ marginRight: 6 }} /> Appels</Flex>}>
          <div style={{ marginTop: '1rem' }}></div>
          <CallsList contactId={wipContact.id} />
        </Tabs.TabPane>
      </Tabs>

    </FormCard>
  );
};

export default ContactDetails;
